import StateComponent from "components/Dashboard/StateComponent"
import React, {
  useState,
  useMemo,
  useContext,
  useEffect,
  useCallback,
  useRef,
} from "react"
import { useNavigate } from "react-router-dom"
import i18n from "i18n"
import {
  DASHBOARD_FILTER,
  DASHBOARD_COLUMN_VISIBILITY,
  DASHBOARD_COLUMN_WIDTHS,
  DASHBOARD_ORDERED_FIELDS,
  DASHBOARD_SORT,
} from "./useDashboardConfig"
import moment from "moment"
import DropdownSettings from "components/Project/Overview/DropdownSettings"
import OpenInNewIcon from "@mui/icons-material/OpenInNew"
import { currencyFormatDE } from "lib/transformers"
import { getCurrency } from "utils/functions"
import AuthContext from "context/auth/authContext"
import SubscriptionContext from "context/subscription/subscriptionContext"
import { OVERVIEW } from "constants/entitlements"
import { subscriptionHasEnabledFeature } from "utils/subscriptionEntitlements"
import { useOrganizacionTags } from "hooks/useOrganizacionTags"
import {
  useBusinessUnitTags,
  useCreateBusinessUnitTag,
  useDeleteBusinessUnitTag,
} from "components/Dashboard/hooks/useBusinessUnitTags"
import { useBrands } from "components/ProfileRefinament/hooks/useBrands"
import { useQueryClient } from "@tanstack/react-query"
import TagField from "common/TagField"
import { useProjectContext } from "context/projects/projectContext"
import { Box } from "@mui/material"

const DEFAULT_OPTIONS = [
  { state: "E10", tag: "To Do" },
  { state: "E20", tag: "In Progress" },
  { state: "E30", tag: "Done" },
]

/**
 * Custom hook for managing dashboard column configuration, sorting, and visibility filters.
 *
 * This hook provides tools for managing column order, sort models, and visibility settings.
 * It uses `useDashboardConfig` for persistent configuration management and exposes functions to update each aspect of the dashboard configuration.
 *
 * @returns {Object} Configuration and handler functions for managing dashboard columns and filters.
 * @returns {Array} columns - An array of column definitions for rendering in the dashboard.
 * @returns {Function} updateColumnOrder - Updates the order of columns based on new indices.
 * @returns {Function} handleColumnOrderChange - Handles column drag-and-drop order changes.
 * @returns {Function} handleSortModelChange - Updates the sorting model of the columns.
 * @returns {Function} handleColumnVisibilityModelChange - Updates the visibility model of the columns.
 * @returns {Object} dashboardConfig - The current dashboard configuration state.
 * @returns {Function} updateConfig - Updates the dashboard configuration for a specific key.
 *
 * @example
 * const {
 *   columns,
 *   updateColumnOrder,
 *   handleColumnOrderChange,
 *   handleSortModelChange,
 *   handleColumnVisibilityModelChange,
 *   dashboardConfig,
 * } = useDashboardConfigFilters();
 *
 * @description
 * - `columns`: Array of column definitions, including rendering and sorting logic.
 * - `updateColumnOrder`: Rearranges columns and updates the order in the persistent configuration.
 * - `handleColumnOrderChange`: Event handler for drag-and-drop column reordering.
 * - `handleSortModelChange`: Updates the sort order of columns based on user interaction.
 * - `handleColumnVisibilityModelChange`: Toggles column visibility and saves it to the configuration.
 * - `dashboardConfig`: Stores the state of the dashboard configuration including column order, visibility, and sort models.
 */
const useDashboardConfigFilters = dashboardConfigState => {
  const navigate = useNavigate()
  const [dashboardConfig, updateConfig] = dashboardConfigState
  const { user } = useContext(AuthContext)
  const { subscriptionState } = useContext(SubscriptionContext)

  const canOpenOverview = useMemo(
    () => subscriptionHasEnabledFeature(subscriptionState, OVERVIEW),
    [subscriptionState],
  )

  const navigateToProject = useCallback(
    projectId => {
      navigate(
        canOpenOverview ? `/overview/${projectId}` : `/budgeting/${projectId}`,
      )
    },
    [navigate, canOpenOverview],
  )

  const { updateBusinessUnit } = useProjectContext()

  // Hook para obtener los tags de organización
  const organizationId = user?.["custom:organizationId"]
  const enabled = !!organizationId
  const { data: organizationTags = [], isFetched: organizationTagsFetched } =
    useOrganizacionTags(organizationId, enabled)

  const { data: businessUnitTags = [], isFetched: businessUnitTagsFetched } =
    useBusinessUnitTags(organizationId, enabled)
  const { data: brands } = useBrands(organizationId)
  const brandNamesById = useMemo(
    () => new Map((brands || []).map(brand => [brand.id, brand.name])),
    [brands],
  )
  const defaultBrandName = useMemo(
    () => (brands || []).find(brand => brand.isDefault)?.name || "",
    [brands],
  )
  const columnWidths = dashboardConfig.columnWidths ?? {}

  const buildTagSignature = tags =>
    tags
      .map(
        tag =>
          `${tag.SK}:${tag?.properties?.name ?? ""}:${tag?.properties?.state ?? ""}`,
      )
      .join("|")

  const tagFilterRefreshSignature = useMemo(
    () =>
      [
        buildTagSignature(organizationTags),
        buildTagSignature(businessUnitTags),
      ].join("::"),
    [organizationTags, businessUnitTags],
  )

  const lastTagFilterRefreshSignature = useRef("")

  const queryClient = useQueryClient()
  const createBusinessUnitTag = useCreateBusinessUnitTag()
  const deleteBusinessUnitTag = useDeleteBusinessUnitTag()

  const handleCreateBusinessUnitTag = async (name, color) => {
    try {
      await createBusinessUnitTag.mutateAsync({
        organizationId,
        tagName: name,
        tagColor: color,
        category: "T",
        objectType: "business_unit",
      })
      queryClient.invalidateQueries(["businessUnitTags", organizationId])
    } catch (err) {
      console.error("Error creating business unit tag:", err)
    }
  }

  const handleDeleteBusinessUnitTag = async tag => {
    try {
      await deleteBusinessUnitTag.mutateAsync({
        organizationId,
        tagSKs: [tag.SK],
      })
      queryClient.invalidateQueries(["businessUnitTags", organizationId])
    } catch (err) {
      console.error("Error deleting business unit tag:", err)
    }
  }

  const handleChangeBusinessUnit = (params, selected) => {
    const chosen = selected[0]
    const prevBusinessUnit = params.row.businessUnit
    const prevBusinessUnitName = params.row.businessUnitName

    // Optimistic UI update: update the grid row immediately
    if (params.api?.updateRows) {
      params.api.updateRows([
        {
          id: params.row.id,
          businessUnit: chosen?.SK,
          businessUnitName: chosen?.properties?.name,
        },
      ])
    } else {
      params.row.businessUnit = chosen?.SK
      params.row.businessUnitName = chosen?.properties?.name
    }

    // Call server and revert on failure
    Promise.resolve(updateBusinessUnit(params.row, chosen?.SK)).catch(() => {
      if (params.api?.updateRows) {
        params.api.updateRows([
          {
            id: params.row.id,
            businessUnit: prevBusinessUnit,
            businessUnitName: prevBusinessUnitName,
          },
        ])
      } else {
        params.row.businessUnit = prevBusinessUnit
        params.row.businessUnitName = prevBusinessUnitName
      }
    })
  }

  const initialColumns = useMemo(
    () => [
      {
        field: "detalle",
        headerName: "Ir al detalle",
        width: 100,
        sortable: false,
        renderCell: params => (
          <OpenInNewIcon
            style={{ cursor: "pointer", color: "#1976d2", marginTop: "10px" }}
            onClick={() => navigateToProject(params.row.id)}
          />
        ),
      },
      {
        field: "projectImage",
        headerName: i18n.t("dashboard.image"),
        width: 100,
        sortable: false,
        renderCell: params => (
          <img
            src={params.row.projectImage}
            alt={params.row.name}
            style={{
              width: 50,
              height: 50,
              objectFit: "cover",
              borderRadius: "4px",
              cursor: "pointer",
            }}
            onClick={() => navigateToProject(params.row.id)}
          />
        ),
      },
      {
        field: "identifierNumber",
        headerName: i18n.t("dashboard.identifierNumber"),
        width: 150,
      },
      { field: "name", headerName: i18n.t("dashboard.name"), width: 200 },
      {
        field: "description",
        headerName: i18n.t("dashboard.description"),
        width: 150,
      },
      {
        field: "state",
        headerName: i18n.t("tags.state"),
        width: 140,
        sortable: true,
        filterable: true,
        type: "string",
        valueGetter: (value, row) => {
          const stateTag = row?.stateTag

          if (!stateTag) return ""

          if (organizationTags.length > 0) {
            const tag = organizationTags.find(t => t.SK === stateTag)

            if (tag && tag.properties && tag.properties.name) {
              return tag.properties.name
            }
          }

          const defaultOption = DEFAULT_OPTIONS.find(
            opt => opt.state === stateTag,
          )
          return defaultOption ? defaultOption.tag : ""
        },
        sortComparator: (v1, v2, cellParams1, cellParams2) => {
          const getStateOrder = cellParams => {
            const row =
              cellParams?.row ||
              cellParams?.api?.getRow?.(cellParams?.id) ||
              cellParams
            const stateTag = row?.stateTag

            if (!stateTag) {
              return 0
            }

            const stateOrder = {
              E10: 1,
              E20: 2,
              E30: 3,
            }

            if (stateOrder[stateTag]) {
              return stateOrder[stateTag]
            }

            if (organizationTags.length > 0) {
              const tag = organizationTags.find(t => t.SK === stateTag)

              if (tag && tag.properties && tag.properties.state) {
                return stateOrder[tag.properties.state] || 0
              }
            }

            return 0
          }

          const order1 = getStateOrder(cellParams1)
          const order2 = getStateOrder(cellParams2)

          return order1 - order2
        },
        renderCell: params => <StateComponent projectRow={params.row} />,
      },
      {
        field: "businessUnit",
        headerName: i18n.t("dashboard.businessUnit"),
        width: 190,
        valueGetter: value => {
          const selected = businessUnitTags.filter(t => t.SK === value)

          return selected[0]?.properties?.name || ""
        },
        renderCell: params => {
          const selected = businessUnitTags.filter(
            t => t.SK === params.row.businessUnit,
          )

          return (
            <TagField
              multiple={false}
              borderless={true}
              onCreateTag={handleCreateBusinessUnitTag}
              onDeleteTag={handleDeleteBusinessUnitTag}
              onChange={selected => handleChangeBusinessUnit(params, selected)}
              options={businessUnitTags}
              selectedTags={selected}
              variant="grid"
            />
          )
        },
      },
      {
        field: "brandId",
        headerName: i18n.t("dashboard.brand"),
        width: 190,
        valueGetter: value => brandNamesById.get(value) || defaultBrandName,
        renderCell: params => (
          <Box
            component="span"
            sx={{
              fontStyle: brandNamesById.has(params.row.brandId)
                ? "normal"
                : "italic",
            }}
          >
            {params.value}
          </Box>
        ),
      },
      {
        field: "createdAt",
        headerName: i18n.t("dashboard.createdAt"),
        width: 100,
        renderCell: params => moment(params.value).format("DD/MM/YYYY"),
      },
      {
        field: "updatedAt",
        headerName: i18n.t("dashboard.updatedAt"),
        width: 100,
        renderCell: params => moment(params.value).format("DD/MM/YYYY"),
      },
      {
        field: "progressPercentage",
        headerName: i18n.t("dashboard.progressPercentage"),
        width: 100,
      },
      {
        field: "finalPrice",
        headerName: i18n.t("dashboard.finalPrice"),
        width: 150,
        type: "number",
        valueFormatter: params => {
          const value = params
          if (value !== undefined && value !== null) {
            const data = currencyFormatDE(
              value,
              getCurrency(user["custom:currency"]),
            )
            return data
          }
          return "0"
        },
      },
      {
        field: "price",
        headerName: i18n.t("dashboard.price"),
        width: 150,
        type: "number",
        valueFormatter: params => {
          const value = params
          if (value !== undefined && value !== null) {
            const data = currencyFormatDE(
              value,
              getCurrency(user["custom:currency"]),
            )
            return data
          }
          return "0"
        },
      },

      {
        field: "priceReduced",
        headerName: i18n.t("dashboard.priceReduced"),
        width: 150,
        type: "number",
        valueFormatter: params => {
          const value = params
          if (value !== undefined && value !== null) {
            const data = currencyFormatDE(
              value,
              getCurrency(user["custom:currency"]),
            )
            return data
          }
          return "0"
        },
      },
      {
        field: "industrialProfit",
        headerName: i18n.t("dashboard.marginObj"),
        width: 150,
        type: "number",
      },
      {
        field: "actions",
        headerName: "",
        width: 50,
        sortable: false,
        hideable: false,
        filterable: false,
        pinnable: false,
        pinned: "right",
        headerAlign: "center",
        align: "center",
        renderCell: params => (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              height: "100%",
            }}
          >
            <DropdownSettings
              imCard={true}
              project={params.row}
              imList={true}
              centerInCell
            />
          </Box>
        ),
      },
    ],
    [
      tagFilterRefreshSignature,
      navigateToProject,
      brandNamesById,
      defaultBrandName,
    ],
  )

  const columnsWithPersistedWidths = useMemo(
    () =>
      initialColumns.map(column => {
        const persistedWidth = columnWidths[column.field]

        if (typeof persistedWidth !== "number") {
          return column
        }

        return {
          ...column,
          width: persistedWidth,
        }
      }),
    [columnWidths, initialColumns],
  )

  const sortDataByColumnOrder = (data, columnOrder) => {
    if (
      !columnOrder ||
      columnOrder.length === 0 ||
      columnOrder.length !== data.length
    ) {
      return data
    }

    return [...data].sort((a, b) => {
      return columnOrder.indexOf(a.field) - columnOrder.indexOf(b.field)
    })
  }

  const sortedData = useMemo(
    () =>
      sortDataByColumnOrder(
        columnsWithPersistedWidths,
        dashboardConfig.orderedFields,
      ),
    [columnsWithPersistedWidths, dashboardConfig.orderedFields],
  )

  const [columns, setColumns] = useState(sortedData)

  useEffect(() => {
    setColumns(sortedData)
  }, [sortedData])

  const updateColumnOrder = async (initialIndex, newIndex) => {
    const columnsClone = [...columns]
    const column = columnsClone.splice(initialIndex, 1)[0]
    columnsClone.splice(newIndex, 0, column)
    const newColumnsOrder = columnsClone.map(item => item.field)
    updateConfig(DASHBOARD_ORDERED_FIELDS, newColumnsOrder)
    setColumns(columnsClone)
  }

  const handleColumnOrderChange = async params => {
    await updateColumnOrder(params.oldIndex, params.targetIndex)
  }

  const handleSortModelChange = newSortModel =>
    updateConfig(DASHBOARD_SORT, newSortModel)

  const handleFilterModelChange = newFilterModel =>
    updateConfig(DASHBOARD_FILTER, newFilterModel)

  const handleColumnWidthChange = params => {
    const field = params?.field ?? params?.colDef?.field
    const width = params?.width

    if (!field || typeof width !== "number") {
      return
    }

    if (columnWidths[field] === width) {
      return
    }

    updateConfig(DASHBOARD_COLUMN_WIDTHS, {
      ...columnWidths,
      [field]: width,
    })
  }

  useEffect(() => {
    const hasActiveFilters =
      (dashboardConfig?.filter?.items?.length ?? 0) > 0 ||
      (dashboardConfig?.filter?.quickFilterValues?.length ?? 0) > 0

    if (!hasActiveFilters) {
      return
    }

    if (!enabled || !organizationTagsFetched || !businessUnitTagsFetched) {
      return
    }

    if (lastTagFilterRefreshSignature.current === tagFilterRefreshSignature) {
      return
    }

    lastTagFilterRefreshSignature.current = tagFilterRefreshSignature
    updateConfig(DASHBOARD_FILTER, {
      ...dashboardConfig.filter,
      items: [...(dashboardConfig.filter?.items ?? [])],
      quickFilterValues: [...(dashboardConfig.filter?.quickFilterValues ?? [])],
    })
  }, [
    businessUnitTagsFetched,
    dashboardConfig.filter,
    organizationTagsFetched,
    tagFilterRefreshSignature,
  ])

  const handleColumnVisibilityModelChange = model =>
    updateConfig(DASHBOARD_COLUMN_VISIBILITY, model)

  return {
    columns,
    updateColumnOrder,
    handleColumnOrderChange,
    handleSortModelChange,
    handleFilterModelChange,
    handleColumnWidthChange,
    handleColumnVisibilityModelChange,
    dashboardConfig,
    updateConfig,
  }
}

export default useDashboardConfigFilters
